/**
 * Custom hook for drone flight calculations
 * 
 * This hook encapsulates the calculation logic for drone flight parameters
 * and operational zones, providing a clean interface for components.
 */
import { useState, useEffect } from 'react';
import { 
  calculateSoftGeoFence, 
  calculateRestrictedArea, 
  calculateAltitudeIncrease,
  calculateTotalRestrictedArea,
  calculateHardGeoFence,
  checkSpaceConstraints
} from '../utils/calculations';
import { FlightParameters, OperationalZones, UnitSystem } from '../types/calculator';

interface UseFlightCalculationsProps {
  initialParameters: FlightParameters;
  initialUnitSystem: UnitSystem;
}

interface UseFlightCalculationsReturn {
  parameters: FlightParameters;
  zones: OperationalZones;
  unitSystem: UnitSystem;
  setParameters: (params: Partial<FlightParameters>) => void;
  setUnitSystem: (system: UnitSystem) => void;
  hasSpaceWarning: boolean;
  resetCalculations: () => void;
}

/**
 * Custom hook that manages flight parameters, unit system, and zone calculations
 * 
 * @param initialParameters - Initial flight parameters
 * @param initialUnitSystem - Initial unit system ('metric' or 'imperial')
 * @returns Object with parameters, zones, unitSystem, setters, and status indicators
 */
export const useFlightCalculations = ({
  initialParameters,
  initialUnitSystem
}: UseFlightCalculationsProps): UseFlightCalculationsReturn => {
  // State management for flight parameters, unit system, and calculated zones
  const [parameters, setParametersState] = useState<FlightParameters>(initialParameters);
  const [unitSystem, setUnitSystemState] = useState<UnitSystem>(initialUnitSystem);
  const [zones, setZones] = useState<OperationalZones>({
    flightArea: 0,
    softGeoFence: 0,
    hardGeoFence: 0,
    restrictedArea: 0,
    altitudeIncrease: 0,
    totalRestrictedArea: 0,
    totalRestrictedAreaDisplay: 0,
    fitsInAvailableSpace: true,
    spaceFits: true,
    unitSystem: initialUnitSystem
  });
  const [hasSpaceWarning, setHasSpaceWarning] = useState<boolean>(false);

  // Handler for updating parameters
  const setParameters = (params: Partial<FlightParameters>) => {
    setParametersState(prevParams => ({
      ...prevParams,
      ...params
    }));
  };

  // Handler for updating unit system
  const setUnitSystem = (system: UnitSystem) => {
    // Only convert if we're actually changing unit systems
    if (system !== unitSystem) {
      const convertedParameters = { ...parameters };
      
      if (system === 'imperial') {
        // Converting from metric to imperial (meters to feet)
        convertedParameters.radius = Math.round(parameters.radius * 3.28084);
        convertedParameters.altitude = Math.round(parameters.altitude * 3.28084);
        convertedParameters.availableSpace = Math.round((parameters.availableSpace || 60) * 3.28084);
        // Velocity stays the same as it's in m/s for both systems
      } else {
        // Converting from imperial to metric (feet to meters)
        convertedParameters.radius = Math.round(parameters.radius / 3.28084);
        convertedParameters.altitude = Math.round(parameters.altitude / 3.28084);
        convertedParameters.availableSpace = Math.round((parameters.availableSpace || 200) / 3.28084);
        // Velocity stays the same as it's in m/s for both systems
      }
      
      // Update both unit system and converted parameters
      setParametersState(convertedParameters);
    }
    
    setUnitSystemState(system);
  };

  // Reset to initial values
  const resetCalculations = () => {
    setParametersState(initialParameters);
    setUnitSystemState(initialUnitSystem);
  };

  // Calculate zones whenever parameters or unit system changes
  useEffect(() => {
    try {
      // Calculate each zone component based on current parameters
      const softGeoFence = calculateSoftGeoFence(parameters.altitude, unitSystem);
      const { totalRestrictedArea, totalRestrictedAreaDisplay } = calculateTotalRestrictedArea(
        parameters.velocity,
        parameters.altitude,
        unitSystem
      );
      const hardGeoFence = calculateHardGeoFence(softGeoFence, unitSystem);
      const altitudeIncrease = calculateAltitudeIncrease(parameters.altitude, unitSystem);
      // Provide default value for availableSpace if it's undefined
      const availableSpace = parameters.availableSpace ?? (unitSystem === 'imperial' ? 500 : 152.4); // Default to 500 ft or 152.4 m
      
      const spaceFits = checkSpaceConstraints(
        parameters.radius,
        totalRestrictedArea,
        availableSpace,
        unitSystem
      );

      // Update zones state
      setZones({
        flightArea: parameters.radius,
        softGeoFence,
        hardGeoFence,
        altitudeIncrease,
        totalRestrictedArea,
        totalRestrictedAreaDisplay,
        spaceFits,
        unitSystem
      });

      // Update zones state
      setZones({
        flightArea: parameters.radius,
        softGeoFence,
        hardGeoFence,
        restrictedArea: calculateRestrictedArea(parameters.velocity, parameters.altitude, unitSystem),
        altitudeIncrease,
        totalRestrictedArea,
        totalRestrictedAreaDisplay,
        fitsInAvailableSpace: spaceFits,
        spaceFits,
        unitSystem
      });
      
      // Set warning flag if space constraints are not met
      setHasSpaceWarning(!spaceFits);
    } catch (error) {
      // Log any calculation errors but don't crash the app
      console.error('Error calculating drone operation zones:', error);
      setHasSpaceWarning(true);
    }
  }, [parameters, unitSystem]);

  return {
    parameters,
    zones,
    unitSystem,
    setParameters,
    setUnitSystem,
    hasSpaceWarning,
    resetCalculations
  };
};

export default useFlightCalculations;
